import React, { useState, useEffect } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { useParams } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import { GroupActivityFeedQuery } from 'services/apollo/queries';
import { LoaderDashboard, EmptyFeed, FeedListItem, FeedListInfinitySpinner } from 'components';
import { GroupPinnedFeed } from './GroupPinnedFeed';

import { useFeatureFlagState, useGroupGetStartedState } from 'context';
import { criticalFeedError, handleFetchMoreError } from 'services/apollo';
import { GroupGetStartedFeed } from '.';
import { GroupTypes } from 'helpers';
import { trackingGroupActivityFeed } from 'services/analytics';

import styles from './GroupActivityFeed.module.scss';
import { useQueryLifecycle } from 'hooks/useQueryLifecycle';

interface GroupActivityFeedProps {
  showPinned?: boolean;
  togglePinned?: () => void;
}

export const GroupActivityFeed: React.FC<GroupActivityFeedProps> = ({
  togglePinned,
  showPinned,
}) => {
  const { groupId } = useParams();
  const { groupsFeatureFlag } = useFeatureFlagState();
  const { groupGetStartedCompleteList } = useGroupGetStartedState();
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const { error, data, fetchMore, networkStatus } = useQuery(GroupActivityFeedQuery, {
    variables: { groupId, includeGroups: groupsFeatureFlag, first: 10, cursor: '' },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  const { isCached: showingCachedData } = useQueryLifecycle(networkStatus);

  /**
   * Analytics tracking
   */
  useEffect(() => {
    if (!showingCachedData) {
      const totalNodes = data?.groupById?.activityFeed?.pageInfo?.total;
      const memberCount = data?.groupById?.members?.length;
      const groupName = data?.groupById?.name;
      const groupType = data?.groupById?.groupType;
      trackingGroupActivityFeed(totalNodes, memberCount, groupName, groupType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showingCachedData]);

  const [ref, inView] = useInView({
    rootMargin: '200px 0px',
    threshold: 0,
  });

  /**
   * Loads more results into the activity feed
   */
  const triggerFetchMore = async () => {
    setIsLoadingMore(true);
    const { endCursor } = data.groupById.activityFeed.pageInfo;

    try {
      await fetchMore({
        variables: { first: 20, cursor: endCursor },
        updateQuery: (prevResult: any, { fetchMoreResult }) => {
          fetchMoreResult.groupById.activityFeed.nodes = [
            ...prevResult.groupById.activityFeed.nodes,
            ...fetchMoreResult.groupById.activityFeed.nodes,
          ];
          setIsLoadingMore(false);
          return fetchMoreResult;
        },
      });
    } catch (e) {
      handleFetchMoreError(e);
    }
  };

  useEffect(() => {
    if (inView === true) {
      triggerFetchMore().then((response) => response);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  if (data === undefined || data?.groupById == null)
    return <LoaderDashboard layout="GroupActivityFeed" />;

  if (error && criticalFeedError(error)) {
    return <LoaderDashboard layout="GroupActivityFeed" />;
  }

  //@ts-ignore
  const groupGetStartedComplete = groupGetStartedCompleteList.includes(groupId);

  const showGetStarted =
    data.groupById?.activityFeed.pageInfo.total < 4 &&
    data.groupById?.members.length > 1 &&
    data.groupById?.haveNodesInCommon &&
    data.groupById?.groupType === GroupTypes.CUSTOM &&
    !groupGetStartedComplete;

  if (showGetStarted) {
    return <GroupGetStartedFeed />;
  }

  if (!data.groupById.activityFeed.nodes.length) {
    return <EmptyFeed message={'Add documents to see group activity'} />;
  }

  return (
    <>
      {/* Pinned Documents */}
      <GroupPinnedFeed
        groupName={data.groupById.name}
        visible={showPinned}
        toggleVisibility={togglePinned}
      />

      {/* Group Activity Feed */}
      <>
        <div className={styles.headingWrapper}>
          <h3 className={styles.heading}>Recent</h3>
        </div>

        {data.groupById.activityFeed.nodes.map((node: any) => (
          <FeedListItem
            node={node}
            nodeId={node.id}
            nodeType={node.__typename}
            key={node.id}
            name={node.title}
            baseUrl={node.baseUrl}
            url={node.url}
            pins={node.pins}
            isPinnable={true}
            isIntegrationConnected={node.isIntegrationConnected}
            integrationType={node.integrationType}
            owner={node.owners[0]}
            collaborators={node.accessorInfo}
            modified={node.modified}
            created={node.created}
            nodeIcon={node.category}
            isShareable={node.sharingEnabled}
            shareTargets={node.shareTargets}
            isCached={showingCachedData}
          />
        ))}
      </>

      {data?.groupById?.activityFeed?.pageInfo?.hasNextPage && (
        <div ref={ref}>
          <FeedListInfinitySpinner isLoadingMore={isLoadingMore} />
        </div>
      )}
    </>
  );
};
